<template>
    <div ref="mfTarget">
        <slot v-if="loading" name="loading">Loading MF...</slot>
        <slot v-else-if="error" name="error" :error="error">An error occurred while loading the MF {{ appName }}</slot>
        <VJoyCollapse v-if="!loading && error && isDevMode" mode="outline">
            <VJoyCollapseItem header="ERROR STACK">
                <ErrorStack :error="error" />
            </VJoyCollapseItem>
        </VJoyCollapse>
    </div>
</template>

<script setup lang="ts">
    import {computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch} from 'vue';
    import {useLogger, useRuntimeConfig} from '#imports';
    import type {
        MicroFrontendAvailableApps,
        MicroFrontendRootPropsMapper,
        MicroFrontendsWithEvents,
        toCustomEvent,
    } from '@malt/nuxt-micro-frontend-types';
    import {VJoyCollapse, VJoyCollapseItem} from '@maltjoy/core-vue';
    import ErrorStack from '../../components/ErrorStack.vue';
    import {useMFEvent} from '../../composables/useMFEvent';
    import {useMicroFrontend} from '../../composables/useMicroFrontend';
    import {handleBuiltInMicroFrontendHostEvents} from '../../internals/handleBuiltInMicroFrontendHostEvents';

    const props = defineProps<{
        app: MicroFrontendAvailableApps;
        props?: MicroFrontendRootPropsMapper[keyof MicroFrontendRootPropsMapper];
    }>();

    // eslint-disable-next-line vue/valid-define-emits
    const emit = defineEmits();

    watch(
        () => props.props,
        () => {
            setWindowMFProps();
            window.dispatchEvent(new CustomEvent(`${props.app}:mf:props-updated`, {detail: props.props}));
        },
        {deep: true},
    );

    const mfTarget = useTemplateRef('mfTarget');
    const loading = ref(true);
    const error = ref<Error>();
    const isDevMode = import.meta.dev;

    const {fetchMicroFrontendAssets, injectMicroFrontendAssets, cleanMicroFrontendAssets} = useMicroFrontend(
        props.app as MicroFrontendAvailableApps,
        () => mfTarget.value,
    );
    useMFEvent(props.app as MicroFrontendsWithEvents, onMFEvent);
    const logger = useLogger();

    // The computed is only here to make TS happy because generic prop is not supported in template
    const appName = computed(() => props.app);

    const config = useRuntimeConfig();
    const hostApp = {name: config.public.application, baseUrl: config.app.baseURL};

    onMounted(async () => {
        await nextTick();
        window.addEventListener(`${props.app}:mf:mounted`, microFrontendLoaded);

        try {
            await fetchMicroFrontendAssets();
            setWindowMFProps();
            injectMicroFrontendAssets();
        } catch (e) {
            logger.error(e);
            error.value = e as Error;
            loading.value = false;
        }
    });

    function microFrontendLoaded() {
        loading.value = false;
        const init = new CustomEvent(`${props.app}:mf:init`, {detail: props.props});
        window.dispatchEvent(init);
    }

    function onMFEvent(event: toCustomEvent<MicroFrontendsWithEvents>) {
        const {name, args} = event.detail;
        if (handleBuiltInMicroFrontendHostEvents(name, args)) {
            return;
        }
        emit(name as any, ...(args as []));
    }

    function setWindowMFProps() {
        window.MF_PROPS = {...window.MF_PROPS, [props.app]: {...props.props, hostApp}};
    }

    function cleanWindowMFProps() {
        window.MF_PROPS = {...window.MF_PROPS, [props.app]: undefined};
    }

    onBeforeUnmount(() => {
        window.removeEventListener(`${props.app}:mf:mounted`, microFrontendLoaded);
        cleanMicroFrontendAssets();
        cleanWindowMFProps();
    });
</script>
